import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useTranslation } from 'next-i18next';

import { Dropzone } from '@/shared/components/Dropzone';
import { COLORS } from '@/shared/constants/colors';
import { files as fileHelpers, FilesNamespace } from '@/shared/helpers';
import { FileAction } from '@/shared/types';
import { Icon } from '@/shared/uikit/Icon';
import { makeInputAcceptFormatStr } from '@/shared/utils/makeInputAcceptFormatStr';

import { useModalsStore } from '../modals';
import { FileItem, FileItemType } from '../modals/containers/UploadFiles/FileItem';
import { FileItemInfo } from '../modals/containers/UploadFiles/types';

// TODO rework import
import { FileList } from './styled';
import { UploadResult, useFiles } from './useFiles';

export type FileUploaderOptions = {
  acceptFiles?: FilesNamespace;
  allowedActions?: FileAction[];
  fileItemType?: FileItemType;
};

export type UploadState = {
  files: FileItemInfo[];
  uploading: boolean;
};
export type FileUploaderRef = {
  upload: (attached?: boolean) => Promise<UploadResult>;
  addFiles?: (items: File[]) => void;
};

export type FileUploaderProps = {
  onUpdate: (state: UploadState) => void;
} & FileUploaderOptions;

export const FileUploader = forwardRef<FileUploaderRef, FileUploaderProps>(
  ({ onUpdate, acceptFiles = 'attachments', allowedActions, fileItemType }, ref) => {
    // TODO i18n file uploader
    const { t } = useTranslation('widgets/modals', { keyPrefix: 'uploadFiles' });
    const modals = useModalsStore();
    const { files, uploading, ...filesActions } = useFiles();
    const acceptKeys = fileHelpers
      .getAllowedMimeTypes(acceptFiles)
      .map((accept) => accept.replace('image/', ''));

    const openEditComment = (file: FileItemInfo) => {
      modals.openModal({
        type: 'EditComment',
        file: file.file,
        comment: file.comment,
        onChange: (value) => filesActions.changeComment(file.id, value),
      });
    };

    useImperativeHandle(
      ref,
      () => ({
        upload: filesActions.uploadFiles,
        addFiles: filesActions.addFiles,
      }),
      [filesActions],
    );

    useEffect(() => {
      onUpdate({ files, uploading });
    }, [files, uploading]);

    return (
      <>
        <Dropzone acceptFiles={acceptFiles} onAddFiles={filesActions.addFiles}>
          <Icon name="upload-simple" size="s32" style={{ color: COLORS.systemDark200 }} />
          <span style={{ marginTop: 8, fontWeight: 700 }}>{t('dropzone.text')}</span>
          <span style={{ marginTop: 4, color: COLORS.systemDark200, fontWeight: 700 }}>
            {acceptFiles === 'assetPhotos'
              ? `${makeInputAcceptFormatStr(acceptKeys, t)} ${t('dropzone.base')}`
              : t('dropzone.types')}
          </span>
          <span
            style={{
              marginTop: 8,
              height: 20,
              fontSize: 13,
              fontWeight: 700,
              color: COLORS.systemSky200,
            }}
          >
            {t('dropzone.btn')}
          </span>
        </Dropzone>
        <FileList>
          {files.map((file) => (
            <FileItem
              fileInfo={file}
              allowedActions={allowedActions}
              itemType={fileItemType}
              onRemove={() => filesActions.removeFile(file.id)}
              onChangeComment={() => openEditComment(file)}
              key={file.id}
            />
          ))}
        </FileList>
      </>
    );
  },
);

FileUploader.displayName = 'FileUploader';
